import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Clock } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import classNames from 'classnames';
import helpers from '../../helpers/countdownHelper';

const TimerStackedUnits = ({
  timeStringAndMessaging,
  shouldUseMessagingString,
  shouldIncludeSvg,
  invertText,
  textColor
}) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const { messagingString } = timeStringAndMessaging;
  const {
    hours,
    minutes,
    seconds,
  } = helpers.getTextBelow(timeStringAndMessaging.timeString);

  const labels = {
    days: 'Days',
    hours: 'Hrs',
    minutes: 'Mins',
    seconds: 'Secs'
  };

  const countdown = {};

  if (timeStringAndMessaging.timeString.days !== undefined) {
    countdown.days = timeStringAndMessaging.timeString.days.toString().padStart(2, '0');
  }

  countdown.hours = hours;
  countdown.minutes = minutes;
  countdown.seconds = seconds;

  const boxSize = isMobile ? 'sui-w-[40px] sui-h-[40px]' : 'sui-w-[60px] sui-h-[60px]';
  const numberSize = isMobile ? 'sui-text-xl' : 'sui-text-5xl';
  const labelSize = isMobile ? 'sui-text-xs' : 'sui-text-base';

  const elementStyles = {};
  if (textColor) {
    elementStyles.color = textColor;
  }

  const style = {
    numberBox: classNames(
      'sui-flex sui-items-center sui-justify-center',
      'sui-bg-white sui-rounded-lg sui-shadow-md sui-border sui-border-gray-100',
      'sui-h2-bold sui-font-bold',
      boxSize,
      numberSize
    ),
    container: classNames(
      'sui-flex sui-items-center sui-gap-2',
      'sui-flex-col md:sui-flex-row md:sui-gap-12 md:sui-items-center'
    ),
    messageContainer: classNames(
      'sui-flex sui-items-center sui-justify-center',
      'md:sui-w-[500px] md:sui-h-[40px]',
      { 'sui-min-h-[40px]': isMobile }
    ),
    timeUnitsContainer: classNames(
      'sui-flex sui-items-start sui-justify-center sui-gap-5'
    ),
    unitContainer: classNames(
      'sui-flex sui-flex-col sui-items-center'
    ),
    iconContainer: classNames(
      'sui-flex sui-items-center sui-justify-center',
      boxSize
    )
  };

  const messageTextStyle = classNames(
    'sui-text-center sui-h2-base sui-font-bold',
    'sui-leading-tight sui-tracking-normal',
    'sui-whitespace-nowrap',
    { 'sui-text-primary': !invertText && !textColor },
    { 'sui-text-inverse': invertText && !textColor },
    { 'sui-text-3xl': isMobile },
    { 'sui-text-5xl': !isMobile }
  );

  const unitLabelStyle = classNames(
    'sui-text-center sui-mt-2',
    'sui-h2-base sui-font-bold sui-leading-tight sui-tracking-normal',
    { 'sui-text-gray-700': !invertText && !textColor },
    { 'sui-text-inverse': invertText && !textColor },
    labelSize
  );

  return (
    <div className={style.container} data-component="TimerStackedUnits">
      {messagingString && shouldUseMessagingString && (
        <div className={style.messageContainer}>
          <span className={messageTextStyle} style={elementStyles}>
            {messagingString}
          </span>
        </div>
      )}
      <div className={style.timeUnitsContainer}>
        {shouldIncludeSvg && (
          <div className={style.unitContainer}>
            <div className={style.iconContainer}>
              <Clock
                color="primary"
                className="sui-w-full sui-h-full"
              />
            </div>
          </div>
        )}
        {Object.entries(countdown).map(([key, value]) => (
          <div key={key} className={style.unitContainer}>
            <div className={style.numberBox}>
              {value}
            </div>
            <div className={unitLabelStyle} style={elementStyles}>
              {labels[key]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

TimerStackedUnits.displayName = 'TimerStackedUnits';

TimerStackedUnits.propTypes = {
  /**
   * A boolean indicating whether or not you want to lead the timer with "Ends in" or "Ends on"
   */
  shouldUseMessagingString: PropTypes.bool,
  /**
   * To show clock image
   */
  shouldIncludeSvg: PropTypes.bool,
  /**
   * To show message and time
   */
  timeStringAndMessaging: PropTypes.shape({
    messagingString: PropTypes.string,
    timeString: PropTypes.shape({
      days: PropTypes.number,
      hours: PropTypes.number,
      minutes: PropTypes.number,
      seconds: PropTypes.number
    }),
  }).isRequired,
  /**
   * To invert text color for dark backgrounds
   */
  invertText: PropTypes.bool,
  /**
   * Custom text color
   */
  textColor: PropTypes.string,
};

TimerStackedUnits.defaultProps = {
  shouldUseMessagingString: false,
  shouldIncludeSvg: false,
  invertText: false,
  textColor: ''
};

export default TimerStackedUnits;